class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    // ✅ Bind submit on ALL forms inside this component (desktop + mobile + potential others)
    const forms = this.querySelectorAll('form');
    if (!forms.length) return;

    forms.forEach((form) => {
      form.addEventListener('submit', this.onSubmitHandler);
    });

    // ✅ SORT FIX: apply sorting on change (keeps your "apply filters only on submit")
    // Dawn usually expects sort to apply immediately and update the URL (?sort_by=...)
    this.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      select.addEventListener('change', (e) => {
        const value = e.target.value;

        // Preserve current filters/search params from URL, only update sort_by
        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', value);

        // Optional: when sorting, reset pagination
        params.delete('page');

        FacetFiltersForm.renderPage(params.toString(), e);
      });
    });

    // ✅ Make the whole pill clickable + toggle the real input
    // Note: This does NOT apply filters anymore (only visual), because we only apply on submit.
    forms.forEach((facetForm) => {
      facetForm.addEventListener('click', (e) => {
        const pill = e.target.closest('.facet-checkbox, .facets__label, .mobile-facets__label');
        if (!pill) return;

        const input = pill.querySelector('input[type="checkbox"], input[type="radio"]');
        if (!input) return;

        // If clicking input itself, let browser handle it
        if (e.target === input) return;

        e.preventDefault();

        if (input.type === 'radio') input.checked = true;
        else input.checked = !input.checked;

        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // ✅ Lock visual state on the actual visible wrapper
      facetForm.addEventListener('change', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.type !== 'checkbox' && input.type !== 'radio') return;

        const wrap = input.closest('.facet-checkbox, .facets__label, .mobile-facets__label');
        if (!wrap) return;

        // radios: clear group
        if (input.type === 'radio') {
          const group = input.name;
          facetForm
            .querySelectorAll(`input[type="radio"][name="${CSS.escape(group)}"]`)
            .forEach((r) =>
              r
                .closest('.facet-checkbox, .facets__label, .mobile-facets__label')
                ?.classList.remove('is-selected')
            );
        }

        wrap.classList.toggle('is-selected', input.checked);
      });
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.add('loading');
    document.getElementById('ProductCount')?.classList.add('loading');
    document.getElementById('ProductCountDesktop')?.classList.add('loading');

    sections.forEach((section) => {
      if (!section.section) return;
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const fromCache = FacetFiltersForm.filterData.find((d) => d.url === url);

      if (fromCache) {
        FacetFiltersForm.renderProductGridContainer(fromCache.html);
        FacetFiltersForm.renderProductCount(fromCache.html);
        FacetFiltersForm.renderFilters(fromCache.html); // ✅ keeps pills/facets in sync
      } else {
        FacetFiltersForm.renderSectionFromFetch(url);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url) {
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderFilters(html); // ✅ keeps pills/facets in sync
      });
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const currentContainer = document.getElementById('ProductGridContainer');
    const newContainer = parsed.getElementById('ProductGridContainer');

    if (currentContainer && newContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
    } else {
      const currentGrid = document.getElementById('product-grid');
      const newGrid = parsed.getElementById('product-grid');
      if (currentGrid && newGrid) currentGrid.outerHTML = newGrid.outerHTML;
    }

    document
      .getElementById('ProductGridContainer')
      ?.querySelectorAll('.scroll-trigger')
      .forEach((el) => el.classList.add('scroll-trigger--cancel'));
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    if (!countEl) return;

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = countEl.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = countEl.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const currentAside = document.getElementById('main-collection-filters');
    const freshAside = parsed.getElementById('main-collection-filters');

    if (currentAside && freshAside) {
      currentAside.innerHTML = freshAside.innerHTML;
      return;
    }

    const current = document.querySelector('.facets-wrapper');
    const fresh = parsed.querySelector('.facets-wrapper');
    if (current && fresh) {
      current.innerHTML = fresh.innerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [{ section: document.getElementById('product-grid')?.dataset?.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const submittedForm = event.target.closest('form');
    if (!submittedForm) return;

    const isMobile = submittedForm.id === 'FacetFiltersFormMobile';

    // ✅ Only build params from the form that was submitted
    const searchParams = this.createSearchParams(submittedForm);

    FacetFiltersForm.renderPage(searchParams, event);

    // ✅ Close mobile drawer safely (no drawer.close())
    if (isMobile) {
      const menuDrawer = submittedForm.closest('menu-drawer');
      if (menuDrawer) {
        const details = menuDrawer.querySelector('details[open]');
        if (details) details.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
